import {
  Column,
  PaginationHook,
  TableColumnSortOrder,
  TableWithPagination,
  useTableHeadCell,
} from "@shared/primitive-components"
import { useLDFlags } from "@shared/integration/launchdarkly"
import { buildHrefLinkForRows } from "@shared/utils/helpers/buildHrefLinkForRows"
import { useCallback, useMemo } from "react"
import { TripVehicle, tripVehicleSortKeyFeValues, TripVehicleSortKeysFE } from "../../../api/insights"
import { PATHS } from "../../../const/paths"
import { InsightsVehicleData, makeVehicleData } from "../../handlers"
import { ChevronFakeLinkCell, VehicleCell, VehicleScoreCell } from "./tableCells"
import {
  averageSpeedExceedencePercentageHeaderProps,
  distanceDrivenHeaderProps,
  distanceSpentSpeedingHeaderProps,
  vehicleHeaderProps,
  vehicleScoreHeaderProps,
} from "./vehicleTableHeaderCells"

export const VEHICLE_TABLE_DTI = {
  CHEVRON: `VEHICLE_TABLE_CHEVRON`,
}

export type VehicleTableProps = Readonly<{
  page: ReadonlyArray<TripVehicle>
  dataCount: number
  pagination: PaginationHook
  sort: { property: TripVehicleSortKeysFE | undefined; order: TableColumnSortOrder | undefined } | undefined
  sortData: (property: TripVehicleSortKeysFE, newSort: TableColumnSortOrder | undefined) => void
  loading?: boolean
}>

export const VehiclesTable = ({ page, sort, dataCount, pagination, sortData, loading }: VehicleTableProps) => {
  const data = makeVehicleData(page)
  const ldFlags = useLDFlags()
  const { useNewTripViews } = ldFlags

  const { HeaderCell } = useTableHeadCell(sortData, tripVehicleSortKeyFeValues, dataCount > 0, sort)

  const VehicleHeader = useCallback(() => <HeaderCell {...{ ...vehicleHeaderProps, property: `vrn` }} />, [HeaderCell])

  const VehicleScoreHeader = useCallback(
    () => <HeaderCell {...{ ...vehicleScoreHeaderProps, property: `vehicleScore` }} />,
    [HeaderCell]
  )
  const DistanceHeader = useCallback(
    () => <HeaderCell {...{ ...distanceDrivenHeaderProps, property: `currentPeriodDistance` }} />,
    [HeaderCell]
  )
  const DistanceSpentSpeedingHeader = useCallback(
    () => <HeaderCell {...{ ...distanceSpentSpeedingHeaderProps, property: `distanceSpentSpeeding` }} />,
    [HeaderCell]
  )
  const SpeedExceedanceAverageHeader = useCallback(
    () => (
      <HeaderCell
        {...{ ...averageSpeedExceedencePercentageHeaderProps, property: `averageSpeedExceedencePercentage` }}
      />
    ),
    [HeaderCell]
  )
  const columns = useMemo<Array<Column<InsightsVehicleData>>>(
    () => [
      {
        Header: VehicleHeader,
        accessor: `vehicle`,
        Cell: VehicleCell,
      },
      {
        Header: VehicleScoreHeader,
        accessor: `vehicleScore`,
        Cell: VehicleScoreCell,
      },
      {
        Header: DistanceHeader,
        accessor: `currentPeriodDistance`,
      },
      {
        Header: DistanceSpentSpeedingHeader,
        accessor: `distanceSpentSpeeding`,
      },
      {
        Header: SpeedExceedanceAverageHeader,
        accessor: `averageSpeedExceedencePercentage`,
      },
      {
        Header: ``,
        accessor: `vrn`,
        Cell: ChevronFakeLinkCell({ dataTestId: VEHICLE_TABLE_DTI.CHEVRON }),
      },
    ],
    [VehicleHeader, VehicleScoreHeader, DistanceHeader, DistanceSpentSpeedingHeader, SpeedExceedanceAverageHeader]
  )
  return (
    <>
      <TableWithPagination<InsightsVehicleData>
        data={data}
        dataCount={dataCount}
        pagination={pagination}
        columns={columns}
        loading={loading}
        renderRowAsAnchor
        buildUrl={(row) =>
          buildHrefLinkForRows(
            `${PATHS.INSIGHTS.BASE}/${PATHS.INSIGHTS.VEHICLES}`,
            useNewTripViews ? `${row.cells[0].value.vrn}/trips` : row.cells[0].value.vrn
          )
        }
      />
    </>
  )
}
